import { isNumber, map } from 'lodash-es';
import moment from 'moment';
import { createSelector } from 'reselect';
import { EXPIRED_STATUS } from 'resources/ConsumerLending/FinancingDetails/Data';
import { Segment } from 'types/JobAndEstimate/segment';
import { isPresent } from 'utils/isPresent';

import { AppDispatch } from 'components/Common/Data/StoreCreator';
import ServicePro from 'components/Common/Types/ServicePro';
import { JobDetailsState, ServiceProTimeTrackersData } from 'components/Job/Types/JobAndEstimateDetails';

import { fetchData, receivedServerData } from '../../components/Common/Data/DataFetcher';
import { ESTIMATE_DETAILS_SCREEN, JOB_DETAILS_SCREEN } from '../../components/Services/Analytics/EventScreenNames';
import { EmptyArray, EmptyObject } from '../EmptyDefaults';

import JobApi from './JobApi';

export const JOB_NAMESPACE = 'job';
export const UPON_RECEIPT_OPTION = 'upon';
export const DATE_OPTION = 'date';
export const NET_OPTION = 'net';

const VIRTUAL_ESTIMATE_SYSTEM_TAG = 'Virtual Estimate';
const JOB_CONVERTED_TO_ESTIMATE = 'job converted to estimate';

export const getIsOutsideBusinessHours = (state: { outsideBusinessHours: unknown }) => state.outsideBusinessHours;

export const getJob = createSelector(
  (state: { job: JobDetailsState }) => state.job,
  (job) => job || {}
);

export const getBasicInfo = createSelector(getJob, (job) => {
  return job.basic_info || {};
});

export const getJobId = createSelector(getBasicInfo, (basicInfo) => {
  return basicInfo.id;
});

export const getJobUuid = createSelector(getBasicInfo, (basicInfo) => {
  return basicInfo.uuid || '';
});

export const getJobName = createSelector(getBasicInfo, (basicInfo) => {
  return basicInfo.displayable_name || '';
});

export const getCompanyName = createSelector(getBasicInfo, (basicInfo) => {
  return basicInfo.company_name;
});

export const allowChangeLocationTrackingHours = createSelector(getBasicInfo, (basicInfo) => {
  return !!basicInfo.change_company_setup;
});

export const isEstimate = createSelector(getBasicInfo, (basicInfo) => {
  return basicInfo.is_estimate;
});

export const hasProposal = createSelector(getBasicInfo, (basicInfo) => {
  return basicInfo?.has_proposal;
});

export const getEstimateExpirationDate = createSelector(getBasicInfo, (basicInfo) => {
  return basicInfo.expiration_date || null;
});

export const getDescription = createSelector(getBasicInfo, (basicInfo) => {
  return basicInfo.description;
});

export const getJobInboxDetails = createSelector(getJob, (job) => {
  return job.job_inbox || null;
});

export const getJobInboxPartner = createSelector(getJobInboxDetails, (jobInbox) => {
  return jobInbox?.partner;
});

export const getBillToJobInboxPartner = createSelector(getJobInboxDetails, (jobInbox) => {
  return isPresent(jobInbox) && !jobInbox?.partner?.bill_directly_to_customer;
});

export const getCustomerInfo = createSelector(getJob, (job) => {
  return job.customer || {};
});

export const getCustomerName = createSelector(getCustomerInfo, (customerInfo) => {
  return customerInfo.customer_name;
});

export const getCustomerFirstName = createSelector(getCustomerInfo, (customerInfo) => {
  return customerInfo.first_name || '';
});

export const getCustomerEmail = createSelector(getCustomerInfo, (customerInfo) => {
  return customerInfo.email || '';
});

export const getCustomerPhoneNumber = createSelector(getCustomerInfo, (customerInfo) => {
  return customerInfo.phone_number || '';
});

export const getCustomerUuid = createSelector(getCustomerInfo, (customerInfo) => customerInfo.uuid || '');

export const getParentCustomerDisplayName = createSelector(getCustomerInfo, (customerInfo) => {
  return customerInfo.parent_customer_display_name;
});

export const haveParentCustomerDisplayName = createSelector(getParentCustomerDisplayName, (displayName) => {
  return isPresent(displayName);
});

export const getCustomerNotificationEnabled = createSelector(getCustomerInfo, (customerInfo) => {
  return customerInfo.notifications_enabled;
});

export const getRecurrenceInfo = createSelector(getJob, (job) => {
  return job.recurrence_info || {};
});

export const getJobOccurrences = createSelector(getRecurrenceInfo, (reccurrence) => reccurrence.job_occurrences);

export const getRecurrenceUUID = createSelector(getRecurrenceInfo, (info) => info.recurrence_uuid);

export const getIsJobRecurring = createSelector(getRecurrenceInfo, (info) => Boolean(info.recurrence_uuid));

export const getAttachmentsInfo = createSelector(getJob, (job) => {
  return job.attachments_info || [];
});

export const getSegments = createSelector(getJob, (job) => {
  return job.segments || {};
});

export const getSegmentArray = createSelector(getSegments, (segments) => {
  return segments.segments || [];
});

export const getSegmentLength = createSelector(getSegmentArray, (segments) => {
  return segments.length || 1;
});

export const segmentHasOption = createSelector(getSegmentLength, (length) => {
  return length > 1;
});

export const getSegmentIds = createSelector(getSegments, (segments) => {
  return segments.segmentIds || [];
});

export const getSegmentUuids = createSelector(getSegmentArray, (segments) => {
  return segments.map((segment) => segment.uuid) || [];
});

export const getSegmentOverviewDue = createSelector(getSegments, (segments) => {
  return segments.overview_total_outstanding_balance || 0;
});

export const getSelectedSegmentIds = createSelector(getSegments, (segments) => {
  return segments.selectedSegmentIds || EmptyArray;
});

export const getSelectedSegmentUuids = createSelector(
  getSegments,
  (segments) => segments.selectedSegmentUuids || EmptyArray
);

const findSegment = (segmentArray: Segment[], segmentId: number | string): Segment | undefined => {
  if (isNumber(segmentId)) {
    // eslint-disable-next-line eqeqeq
    return segmentArray.find((segment) => segment.id == segmentId);
  }
  return segmentArray.find((segment) => segment.uuid === segmentId);
};

export const getCurrentSegmentDue = createSelector([getBasicInfo, getSegmentArray], (basicInfo, segmentArray) => {
  const currentSegmentId = basicInfo.current_segment?.id;
  const currentSegment = findSegment(segmentArray, currentSegmentId);
  return isPresent(currentSegment) ? currentSegment.outstanding_balance : 0;
});

export const getInvoiceableAmount = (segmentUuids: string[]) =>
  createSelector([getSegmentArray], (segmentArray) => {
    return segmentUuids
      .map((uuid) => findSegment(segmentArray, uuid))
      .filter((segment) => isPresent(segment))
      .map((segment) => segment?.invoiceable_amount || 0)
      .reduce((sum, current) => sum + current, 0);
  });

export const getCurrentSegment = createSelector(getBasicInfo, (basicInfo) => {
  return basicInfo.current_segment || {};
});

export const getCurrentSegmentUuid = createSelector(getCurrentSegment, (currentSegment) => {
  return currentSegment.uuid;
});

export const getCustomerServiceAgreementInfo = createSelector(getJob, (job) => {
  return job.customer_service_agreement_info || {};
});

export const getScheduleInfo = createSelector(getJob, (job) => {
  return job.schedule_info || {};
});

export const getStartTime = createSelector(getScheduleInfo, (scheduleInfo) => {
  return scheduleInfo.start_time;
});

export const hasJobStarted = createSelector(getScheduleInfo, (scheduleInfo) => isPresent(scheduleInfo.started_at));

export const getCompletionTime = createSelector(getScheduleInfo, (scheduleInfo) => {
  return scheduleInfo.completed_at;
});

export const hasJobFinished = createSelector(getScheduleInfo, (scheduleInfo) => isPresent(scheduleInfo.completed_at));

export const getpaymentInfo = createSelector(getJob, (job) => {
  return job.payment_information || {};
});

export const getDebitTransactions = createSelector(
  getpaymentInfo,
  (paymentInfo) => paymentInfo.debit_transactions || EmptyArray
);

export const getServiceAddress = createSelector(getBasicInfo, (job) => {
  return job.service_address || {};
});

export const getCustomerSignatures = createSelector(getJob, (job) => {
  return job.customer_signatures || {};
});

export const getLineItems = createSelector(getJob, (job) => {
  return job.line_items || {};
});

export const getMessageFromPro = createSelector(getLineItems, (lineItems) => lineItems.message_from_pro || '');

export const getConsumerLendingInfo = createSelector(getJob, (job) => {
  return job.consumer_lending || EmptyObject;
});

export const getWisetackLendingInfo = createSelector(getConsumerLendingInfo, (consumerLendingInfo) => {
  return consumerLendingInfo.wisetack || EmptyObject;
});

export const getWisetackEnabled = createSelector(
  getWisetackLendingInfo,
  (wisetackLendingInfo) => wisetackLendingInfo.financing_enabled || false
);

export const getLendingButtonLocation = createSelector(isEstimate, (onEstimatePage) => {
  return onEstimatePage ? ESTIMATE_DETAILS_SCREEN : JOB_DETAILS_SCREEN;
});

export const getInvoiceNumber = createSelector(getLineItems, (lineItems) => {
  return lineItems.invoice_number || '';
});

export const getSegmentInvoiceNumber = createSelector(
  [getLineItems, getSegmentLength, getInvoiceNumber],
  (lineItems, segmentsLength, invoiceNumber) => {
    const hasSegments = segmentsLength > 1;
    const segmentInvoiceNumber = lineItems.segment_invoice_number;
    const segmentNumber = hasSegments && segmentInvoiceNumber ? `-${segmentInvoiceNumber}` : '';

    return `${invoiceNumber}${segmentNumber}`;
  }
);

export const getInvoiceDue = createSelector(getLineItems, (lineItems) => lineItems.invoice_due || '');

export const getMaterialLineItems = createSelector(
  getLineItems,
  (lineItems) => lineItems.material_line_items || EmptyArray
);

export const getLaborLineItems = createSelector(getLineItems, (lineItems) => lineItems.labor_line_items || EmptyArray);

export const getFixedDiscountItems = createSelector(
  getLineItems,
  (lineItems) => lineItems.fixed_discount_line_items || EmptyArray
);

export const getPercentDiscountItems = createSelector(
  getLineItems,
  (lineItems) => lineItems.percent_discount_line_items || EmptyArray
);

export const getTaxInfo = createSelector(getJob, (job) => {
  return job.tax_info || {};
});

export const getActivityEvents = createSelector(getJob, (job) => {
  return job.activity_events || [];
});

export const getDisplayConfiguration = createSelector(getJob, (job) => {
  return job.display_configuration || {};
});

export const getMapEmployees = createSelector(getJob, (job) => {
  return job.employees || {};
});

export const getOrganizationMetadata = createSelector(getJob, (job) => {
  return job.organization_meta_data || {};
});

export const getEstimateOptionInfo = createSelector(getJob, (job) => {
  return job.estimate_option_info || {};
});

export const getOrganizationUuid = createSelector(getOrganizationMetadata, (organizationMetadata) => {
  return organizationMetadata.uuid;
});

export const getCompositeServiceRequestUUID = createSelector(getBasicInfo, (basicInfo) => {
  return basicInfo.composite_service_request_uuid || '';
});

export const getTags = createSelector(getBasicInfo, (basicInfo) => basicInfo.request_tags_detail || EmptyArray);

export const getIsVirtualEstimate = createSelector(
  getTags,
  isEstimate,
  (tags, estimate) => estimate && tags.filter((tag) => tag.name === VIRTUAL_ESTIMATE_SYSTEM_TAG).length > 0
);

export const getDispatchablePros = createSelector(
  getScheduleInfo,
  (scheduleInfo) => scheduleInfo.dispatchable_pros || EmptyArray
);

const getAssignedProIds = createSelector(
  getScheduleInfo,
  (scheduleInfo) => scheduleInfo.assigned_pro_ids || EmptyArray
);

export const getAssignedPros = createSelector(
  [getDispatchablePros, getAssignedProIds],
  (dispatchablePros, assignedProIds) => dispatchablePros.filter((pro) => assignedProIds.includes(pro.id))
);

export const getServiceProTimeTrackers = createSelector(getJob, (job) => job.service_pro_time_trackers?.data || []);

export const getHistoricDispatchedPros = createSelector(
  getMapEmployees,
  getAssignedProIds,
  getServiceProTimeTrackers,
  (employeesObject, assignedProIds, serviceProTimeTrackers) => {
    const assignedPros = employeesObject?.employees?.filter((pro) => assignedProIds.includes(pro.id)) || EmptyArray;
    const missingProUuids = map(serviceProTimeTrackers, 'pro_uuid').filter(
      (uuid) => !map(assignedPros, 'uuid').includes(uuid)
    );
    const addKeys = (object: ServiceProTimeTrackersData) => ({
      ...object,
      uuid: object.id,
      avatar: object.map_avatar,
      unassigned: true,
    });
    const timeTrackersObject: { [key: string]: ServicePro } = serviceProTimeTrackers.reduce(
      // TODO: Fix any typings here
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (obj, item) => Object.assign(obj, { [item.pro_uuid]: addKeys(item.employee_details.data as any) }),
      {}
    );
    const missingPros = missingProUuids.map((uuid) => timeTrackersObject[uuid]) as ServicePro[];

    return assignedPros.concat(missingPros);
  }
);

export const getDateFromTransaction = createSelector(
  (transaction: { when: string }) => transaction.when,
  (date) => moment.utc(date).format() || EmptyObject
);

export const getKindFromTransaction = createSelector(
  (transaction: { kind: string }) => transaction.kind,
  (kind) => kind || EmptyObject
);

export const fetchJob = {
  fetchingApiFunc: JobApi.show,
  selector: getJob,
  paramsFromProps: (props: { id: number }) => {
    return [props.id];
  },
  dataNamespace: '',
};

export function getDifferenceInDays(date: string | Record<string, never>) {
  return moment().utc().diff(date, 'days');
}

export function isPastDays(transaction: { when: string }, thresholdDays: number) {
  const diffInDays = getDifferenceInDays(getDateFromTransaction(transaction));
  return diffInDays > thresholdDays;
}

export function isACHPayment(transaction: { kind: string }) {
  return getKindFromTransaction(transaction) === 'checkout ach';
}

export function receivedJob(job: Partial<JobDetailsState>) {
  return receivedServerData(job, JOB_NAMESPACE);
}

export function refetchJob(jobId: number | string) {
  const params = { id: jobId };

  return (dispatch: AppDispatch) => {
    return dispatch(fetchData(fetchJob, params));
  };
}

export const getJobLeadSource = createSelector(getJob, (job) => {
  return job.lead_source;
});

export const hasServiceProTimeTrackers = createSelector(getJob, (job) =>
  isPresent(job.service_pro_time_trackers?.data)
);

export const getJobCommissions = createSelector(getJob, (job) => {
  return job.job_commissions || {};
});

export const getEmployeeCommissions = createSelector(getJobCommissions, (commissions) => {
  return commissions.employee_commissions || [];
});

export const getApplyDiscounts = createSelector(getJobCommissions, (commissions) => {
  return commissions.apply_discounts;
});

export const getChangedSinceLastView = createSelector(getJobCommissions, (commissions) => {
  return commissions.changed_since_last_viewed;
});

export const getDisplayDiscountSwitch = createSelector(
  [getFixedDiscountItems, getPercentDiscountItems],
  (fixedDiscountList, percentDiscountList) => {
    return fixedDiscountList.length + percentDiscountList.length > 0;
  }
);

export const getEstimateStatus = createSelector(getpaymentInfo, (paymentInfo) => {
  return paymentInfo.estimate_status || '';
});

export const hasEstimateExpiredStatus = createSelector(getEstimateStatus, (estimateStatus = '') => {
  return estimateStatus.toLowerCase() === EXPIRED_STATUS;
});

export const isJobConvertedToEstimate = createSelector(getActivityEvents, (activityEvents) => {
  return (
    activityEvents.some(({ kind }) => kind.toLowerCase() === JOB_CONVERTED_TO_ESTIMATE) &&
    window?.location?.href.includes('job')
  );
});
